import React, { useState } from 'react';
import type {SearchResultItem} from '../types/api.types';
import { PDFModal } from './PDFModal';
import { getApiBaseUrl } from '../services/api.service';

interface ResultItemProps {
  result: SearchResultItem;
}

export const ResultItem: React.FC<ResultItemProps> = ({ result }) => {
  const { document, metadata, score, download_url } = result;
  const [isPDFModalOpen, setIsPDFModalOpen] = useState(false);

  // Construir título y URL basados en metadata
  const radicado = metadata.radicado || metadata.doc_id || 'Documento';
  const documentUrl = `#/document/${result.id}`;

  // Construir URL completa del PDF
  const fullPdfUrl = download_url ? `${getApiBaseUrl()}${download_url}` : null;

  const handleViewPDF = () => {
    if (fullPdfUrl) {
      setIsPDFModalOpen(true);
    }
  };

  const handleDownload = async () => {
    if (!fullPdfUrl) return;

    try {
      // Descargar el archivo
      const response = await fetch(fullPdfUrl);
      if (!response.ok) throw new Error('Error al descargar el archivo');

      const blob = await response.blob();

      // Crear un enlace temporal para descargar
      const url = URL.createObjectURL(blob);
      const a = window.document.createElement('a');
      a.href = url;
      a.download = `${radicado}.pdf`;

      window.document.body.appendChild(a);
      a.click();

      // Limpiar después de un pequeño delay
      setTimeout(() => {
        window.document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }, 100);
    } catch (error) {
      console.error('Error al descargar el archivo:', error);
      alert('No se pudo descargar el archivo. Por favor, intente nuevamente.');
    }
  };

  return (
    <div className="group">
      {/* Breadcrumb/URL */}
      <div className="text-sm text-gray-600 mb-1 truncate">
        biblioteca-pqr/documentos/{radicado}
      </div>

      {/* Título */}
      <a
        href={documentUrl}
        className="text-base sm:text-xl text-[#0943B5] hover:underline font-medium visited:text-purple-800 line-clamp-2"
      >
        {radicado} - {metadata.nombre_solicitante || 'Documento PQR'}
      </a>

      {/* Snippet del documento */}
      <p className="text-sm text-gray-700 mt-2 line-clamp-3">
        {document}
      </p>

      {/* Metadata */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-xs text-gray-600">
        {metadata.radicado_entrada && (
          <span>Radicado Entrada: {metadata.radicado_entrada}</span>
        )}
        {metadata.doc_id && (
          <span>ID: {metadata.doc_id}</span>
        )}
      </div>

      {/* Score y acciones */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mt-3">
        {score !== null && (
          <span className="text-sm font-medium text-[#0943B5]">
            Relevancia: {((1 - score) * 100).toFixed(0)}%
          </span>
        )}

        <div className="flex items-center gap-3 text-sm text-[#0943B5]">
          <button
            onClick={handleViewPDF}
            disabled={!fullPdfUrl}
            className={`hover:underline ${!fullPdfUrl ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            📄 Ver PDF
          </button>
          <button
            onClick={handleDownload}
            disabled={!fullPdfUrl}
            className={`hover:underline ${!fullPdfUrl ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            ⬇ Descargar
          </button>
        </div>
      </div>

      {/* Separator */}
      <div className="border-b border-gray-200 mt-6"></div>

      {/* PDF Modal */}
      {fullPdfUrl && (
        <PDFModal
          isOpen={isPDFModalOpen}
          onClose={() => setIsPDFModalOpen(false)}
          pdfUrl={fullPdfUrl}
          title={`${radicado} - ${metadata.nombre_solicitante || 'Documento PQR'}`}
        />
      )}
    </div>
  );
};
